// hooks/user/useUpdateUsername.js
import { useMutation, useQueryClient } from "react-query";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import useToast from "../useToast";

/**
 * Custom hook for updating username
 *
 * Implements retry logic for network failures, comprehensive error handling,
 * and toast notifications for user feedback.
 * Requirements: 4.6, 4.7, 4.8, 4.9, 4.10
 *
 * @returns {Object} Mutation object with updateUsername function
 */
export const useUpdateUsername = () => {
  const { getAccessTokenSilently } = useAuth0();
  const apiUrl = process.env.REACT_APP_API_URL;
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation(
    async (newUsername) => {
      const token = await getAccessTokenSilently();

      const response = await axios.put(
        `${apiUrl}/api/users/update-username`,
        { username: newUsername },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          timeout: 10000, // 10 second timeout
        }
      );

      return response.data;
    },
    {
      retry: (failureCount, error) => {
        // Retry network errors up to 2 times, but not validation errors
        if (error.response?.status === 400 || error.response?.status === 409) {
          return false; // Don't retry validation or conflict errors
        }
        return failureCount < 2; // Retry other errors up to 2 times
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000), // Exponential backoff: 1s, 2s
      onSuccess: (data) => {
        toast.success("Username set successfully!", {
          duration: 3000,
        });
        // Invalidate user query to refetch with new username
        queryClient.invalidateQueries("fetchUser");
      },
      onError: (error) => {
        // Determine error type and show appropriate toast
        let errorMessage = "Failed to set username. Please try again.";

        if (error.code === "ECONNABORTED") {
          errorMessage =
            "Request timed out. Please check your connection and try again.";
        } else if (error.response) {
          // Server responded with error status
          const status = error.response.status;
          const serverMessage =
            error.response?.data?.errors?.[0]?.msg ||
            error.response?.data?.message;

          if (status === 400) {
            // Validation error
            errorMessage =
              serverMessage ||
              "Invalid username format. Please try a different username.";
          } else if (status === 409) {
            // Conflict - username taken (race condition)
            errorMessage =
              "Username was just taken by another user. Please choose a different one.";
          } else if (status === 401 || status === 403) {
            // Authentication error
            errorMessage =
              "Authentication error. Please refresh the page and try again.";
          } else if (status >= 500) {
            // Server error
            errorMessage = "Server error. Please try again in a moment.";
          } else if (serverMessage) {
            errorMessage = serverMessage;
          }
        } else if (error.request) {
          // Request made but no response received
          errorMessage =
            "Network error. Please check your connection and try again.";
        }

        toast.error(errorMessage, {
          duration: 5000,
        });
      },
    }
  );
};
